import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ServiceTemplate } from '../types/entities';

interface ServiceTemplateSelectorProps {
  serviceTemplates: ServiceTemplate[];
  onAddService: (serviceTemplate: ServiceTemplate) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ServiceTemplateSelector: React.FC<ServiceTemplateSelectorProps> = ({
  serviceTemplates,
  onAddService,
  placeholder,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const filteredServiceTemplates = useMemo(() => {
    if (!searchValue.trim()) {
      return serviceTemplates;
    }
    
    const searchLower = searchValue.toLowerCase();
    return serviceTemplates.filter((template) =>
      template.name.toLowerCase().includes(searchLower) ||
      (template.description && template.description.toLowerCase().includes(searchLower))
    );
  }, [serviceTemplates, searchValue]);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  };

  const formatPrice = (price: string): string => {
    return `${Math.round(parseFloat(price))} zł`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('forms.serviceTemplates')}
      </Typography>
      <Autocomplete
        options={filteredServiceTemplates}
        getOptionLabel={(option) => option.name}
        value={null}
        onChange={(_, newValue) => {
          if (newValue) {
            onAddService(newValue);
            setSearchValue(''); // Clear search after selection
          }
        }}
        inputValue={searchValue}
        onInputChange={(_, newInputValue) => {
          setSearchValue(newInputValue);
        }}
        filterOptions={(options) => options} // We handle filtering ourselves
        noOptionsText={t('forms.noServiceTemplatesFound')}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || t('forms.searchServiceTemplates')}
            variant="outlined"
            fullWidth
            size="small"
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box display="flex" flexDirection="column" width="100%">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight="medium">
                  {option.name}
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip
                    label={formatDuration(option.estimated_duration)}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                  <Chip
                    label={formatPrice(option.price)}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                </Box>
              </Box>
              {option.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            padding: '4px 8px',
          },
        }}
      />
      {searchValue && filteredServiceTemplates.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No service templates found matching "{searchValue}"
        </Typography>
      )}
    </Box>
  );
};

export default ServiceTemplateSelector;
